import React, { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useLocation, useNavigate } from 'react-router-dom';
import AgentStripeDeposit from './AgentStripeDeposit';
import {
  confirmAgentDeposit,
  createAgentDepositIntent,
} from '../../api/bookingAgent';
import { AgentHandoffState, buildEmbedReturnUrl, getAgentHandoffFromLocation, setAgentResume } from '../../utils/agentSessionStorage';
import { getURLstylistCode } from '../../utils/globalFunctions';
import { showToast } from '../../utils';
import { images } from '../../utils/constants/assets';
import { ROUTES_CONSTANTS } from '../../utils/constants/routesString';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';

const stripePromise = loadStripe(process.env.REACT_APP_SECRET_KEY as string);

export default function AgentPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const handoff = getAgentHandoffFromLocation(location.search, location.state as AgentHandoffState);
  const stylistCode = getURLstylistCode();

  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [loading, setLoading] = useState(true);

  const amount = handoff.amount || '0.00';
  const currency = (handoff.currency || 'AUD').toUpperCase();
  const formattedAmount = Number(amount).toFixed(2);

  useEffect(() => {
    if (!handoff.sessionId || !handoff.amount) {
      navigate(`${ROUTES_CONSTANTS.index}${stylistCode}`);
      return;
    }

    createAgentDepositIntent(handoff.sessionId, handoff.amount)
      .then((result) => {
        setClientSecret(result.client_secret);
        setPaymentIntentId(result.payment_intent_id);
      })
      .catch(() => {
        showToast('Could not start payment. Please return to chat and try again.', 'error');
        navigate(`${ROUTES_CONSTANTS.index}${stylistCode}`);
      })
      .finally(() => setLoading(false));
  }, [handoff, navigate, stylistCode]);

  const handleBack = () => {
    navigate(`${ROUTES_CONSTANTS.index}${stylistCode}`);
  };

  const handleSuccess = async (intentId: string) => {
    try {
      await confirmAgentDeposit(handoff.sessionId, intentId);
      const returnUrl = new URLSearchParams(location.search).get('return_url');
      if (returnUrl) {
        window.location.href = buildEmbedReturnUrl(returnUrl, handoff.sessionId, 'Deposit paid.');
        return;
      }
      setAgentResume({
        sessionId: handoff.sessionId,
        message: 'Deposit paid.',
        openChat: true,
      });
      navigate(`${ROUTES_CONSTANTS.index}${stylistCode}`);
    } catch {
      showToast('Payment succeeded but confirmation failed. Open chat to continue.', 'error');
      navigate(`${ROUTES_CONSTANTS.index}${stylistCode}`);
    }
  };

  return (
    <div className="booking-wrapper">
      <div className="step_6 sign_up_screen one">
        <div className="upper-wrapper">
          <div className="back-logo">
            <div className="go-back" onClick={handleBack}>
              <img src={images.go_back} alt="" />
            </div>
          </div>

          <h1>{STRING_CONSTANTS.we_are_done}</h1>
          <p className="cancellation">
            {STRING_CONSTANTS.this_booking_requires}
          </p>

          <div className="service-summery">
            <span>{STRING_CONSTANTS.deposit_heading}</span>
            <ul>
              <li>
                <span>Deposit due now</span>
                <b>
                  {currency} {formattedAmount}
                </b>
              </li>
            </ul>
          </div>

          {loading && (
            <div className="pay-popup-outer">
              <div className="overlay" />
              <div className="pay-popup">
                <div className="gif-img">
                  <img src="/assets/images/loading.gif" alt="" />
                </div>
                Preparing secure payment…
              </div>
            </div>
          )}

          {!loading && clientSecret && (
            <div className="service-summery payment-method">
              <span>{STRING_CONSTANTS.Payment_Method}</span>
              <Elements stripe={stripePromise}>
                <AgentStripeDeposit
                  clientSecret={clientSecret}
                  paymentIntentId={paymentIntentId}
                  amount={amount}
                  currency={currency}
                  onSuccess={handleSuccess}
                />
              </Elements>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
